import { ComponentProps, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { FCX, getFormattedThousand } from '@datassential/platform-ui-lib';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { ISocialPost, SocialPosts } from '@models/SocialPosts';
import ExpandableSocialPostCard from '@components/SocialPostCard/ExpandableSocialPostCard';
import CopyButton from '@components/new/Table/CopyButton';
import CopyNotification from '@components/new/Table/CopyNotification';
import useNotification from '@hooks/useNotification';
import { trimInnerHtml, trimLineBreaks } from '@helpers';
import { IconClose } from '@icons';
import './CategoryLensExamplePosts.scss';

const PAGE_SIZE = 4;

const COPY_DATA_CONFIG: Array<{
    key: keyof ISocialPost;
    title: string;
    valueFormatter?: (value: string) => string;
}> = [
    { key: 'source', title: 'Source' },
    { key: 'engagement', title: 'Engagement Count' },
    { key: 'date', title: 'Date', valueFormatter: SocialPosts.formatDate },
    { key: 'text', title: 'Description', valueFormatter: (value) => trimLineBreaks(trimInnerHtml(value)) },
    { key: 'url', title: 'Post URL' },
];

const formatGrowth = (value: number): string => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${getFormattedThousand(Math.round(value))}%`;
};

const CategoryLensExamplePosts: FCX<{
    data: ISocialPost[];
    keywordName: string;
    growth: number;
    onClose: () => void;
}> = ({ data, keywordName, growth, onClose }) => {
    const [page, setPage] = useState(0);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const notification = useNotification();

    const hasPosts = data.length > 0;
    const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages - 1);
    const pageItems = useMemo(
        () => data.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE),
        [data, currentPage],
    );

    const copyData = useMemo(
        (): ComponentProps<typeof CopyButton>['data'] => [
            COPY_DATA_CONFIG.map(({ title }) => title),
            ...data.map(post =>
                COPY_DATA_CONFIG.map(({ key, valueFormatter }) => {
                    const value = post[key];
                    return valueFormatter ? valueFormatter(`${value}`) : `${value}`;
                })
            ),
        ],
        [data],
    );

    return (
        <div className="CategoryLensExamplePosts" data-testid="blockCategoryLensExamplePosts">
            <div className="CategoryLensExamplePosts__header">
                <span className="CategoryLensExamplePosts__title">
                    EXAMPLE POSTS: <span className="CategoryLensExamplePosts__keyword">“{keywordName}”</span>
                </span>
                <div className="CategoryLensExamplePosts__header-actions">
                    {hasPosts && (
                        <div
                            className="CategoryLensExamplePosts__copy"
                            onClick={notification.handleShowNotification}
                        >
                            <CopyButton
                                data={copyData}
                                testId="buttonCategoryLensExamplePostsCopy"
                            />
                        </div>
                    )}
                    <button
                        type="button"
                        className="CategoryLensExamplePosts__close"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="buttonCategoryLensExamplePostsClose"
                    >
                        <IconClose className="Icon24"/>
                    </button>
                </div>
            </div>

            <div className="CategoryLensExamplePosts__growth">
                <span className="CategoryLensExamplePosts__growth-label">P1M Growth</span>
                <span
                    className={cn(
                        'CategoryLensExamplePosts__growth-badge',
                        growth < 0 && 'is-negative',
                    )}
                    data-testid="textCategoryLensExamplePostsGrowth"
                >
                    {formatGrowth(growth)}
                </span>
            </div>

            {hasPosts ? (
                <div className="CategoryLensExamplePosts__body">
                    <button
                        type="button"
                        className="CategoryLensExamplePosts__nav CategoryLensExamplePosts__nav--prev"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={currentPage === 0}
                        aria-label="Previous posts"
                    >
                        <IoChevronBack/>
                    </button>
                    <div ref={scrollRef} className="CategoryLensExamplePosts__grid">
                        {pageItems.map((post) => (
                            <ExpandableSocialPostCard
                                key={post.id}
                                post={post}
                                scrollRef={scrollRef}
                                isDark
                                className="CategoryLensExamplePosts__card"
                                expandedClassName="CategoryLensExamplePosts__card-expanded"
                                testIdPrefix="categoryLensExamplePost"
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        className="CategoryLensExamplePosts__nav CategoryLensExamplePosts__nav--next"
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={currentPage >= totalPages - 1}
                        aria-label="Next posts"
                    >
                        <IoChevronForward/>
                    </button>
                </div>
            ) : (
                <div className="CategoryLensExamplePosts__empty">No social posts found.</div>
            )}

            <CopyNotification isOpen={notification.isOpen}/>
        </div>
    );
};

export default CategoryLensExamplePosts;
